import { PendingButton } from "@/components/common/PendingButton/PendingButton"
import { toast } from "@/components/common/Toast"
import { PermissionEditor } from "@/components/feature/TeamManagement/PermissionEditor"
import { RoleChooser } from "@/components/feature/TeamManagement/RoleChooser"
import { getTeamErrorCode } from "@/components/feature/TeamManagement/teamErrors"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { toastApiError } from "@/features/material-compliance/api/apiError"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  useGetMemberPermissions,
  useGetPermissionCatalog,
  useResendInvite,
  useRevokeInvite,
  useUpdateMemberPermissions,
  useUpdateRole,
} from "@/services/api/teamService"
import type { OrgRole, PermissionCatalog, TeamMember } from "@/types/team"
import { useState } from "react"
import { SettingsSection } from "../../components/SettingsPage"
import {
  SettingsLoadError,
  SettingsSkeleton,
} from "../../components/SettingsStates"
import { useUnsavedChanges } from "../../components/UnsavedChanges"

function RoleSection({ member }: { member: TeamMember }) {
  const { translate } = useTranslation()
  const updateRole = useUpdateRole()
  const [pendingRole, setPendingRole] = useState<OrgRole | null>(null)

  // Awaited, not per-call callbacks: those never fire once the sheet closes.
  async function onConfirm() {
    if (!pendingRole) return
    try {
      await updateRole.mutateAsync({ userSub: member.id, role: pendingRole })
      setPendingRole(null)
      toast.success(translate("TEAM/ROLE_UPDATED"))
    } catch (error) {
      if (getTeamErrorCode(error) === "LAST_ADMIN")
        toast.error(translate("TEAM/LAST_ADMIN"))
      else toastApiError(error, translate, "TEAM/GENERIC_ERROR")
    }
  }

  const toAdmin = pendingRole === "admin"
  return (
    <SettingsSection title={translate("TEAM/ROLE_LABEL")}>
      <RoleChooser
        value={member.role}
        onChange={setPendingRole}
        disabled={member.isSelf}
      />
      <AlertDialog
        open={!!pendingRole}
        onOpenChange={(open) =>
          !open && !updateRole.isPending && setPendingRole(null)
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translate(
                toAdmin ? "TEAM/MAKE_ADMIN_TITLE" : "TEAM/MAKE_MEMBER_TITLE",
                { email: member.email },
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {translate(
                toAdmin ? "TEAM/MAKE_ADMIN_BODY" : "TEAM/MAKE_MEMBER_BODY",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateRole.isPending}>
              {translate("TEAM/CANCEL")}
            </AlertDialogCancel>
            <PendingButton
              pending={updateRole.isPending}
              onClick={() => void onConfirm()}
            >
              {translate(
                toAdmin
                  ? "TEAM/MAKE_ADMIN_CONFIRM"
                  : "TEAM/MAKE_MEMBER_CONFIRM",
              )}
            </PendingButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsSection>
  )
}

function InvitationSection({ member }: { member: TeamMember }) {
  const { translate } = useTranslation()
  const resendInvite = useResendInvite()
  const revokeInvite = useRevokeInvite()
  // Revoking kills the invitation link, so it asks first.
  const [confirmingRevoke, setConfirmingRevoke] = useState(false)
  const invitationId = member.invitationId
  if (!invitationId) return null

  async function resend(id: string) {
    try {
      await resendInvite.mutateAsync(id)
      toast.success(translate("TEAM/RESEND_SUCCESS"))
    } catch (error) {
      toastApiError(error, translate, "TEAM/GENERIC_ERROR")
    }
  }

  async function revoke(id: string) {
    try {
      await revokeInvite.mutateAsync(id)
      setConfirmingRevoke(false)
      toast.success(translate("TEAM/REVOKE_SUCCESS"))
    } catch (error) {
      toastApiError(error, translate, "TEAM/GENERIC_ERROR")
    }
  }

  return (
    <SettingsSection
      title={translate("SETTINGS/MEMBERS/INVITATION_TITLE")}
      footer={
        <>
          <PendingButton
            variant="outline"
            pending={resendInvite.isPending}
            disabled={revokeInvite.isPending}
            onClick={() => void resend(invitationId)}
          >
            {translate("TEAM/ACTION_RESEND")}
          </PendingButton>
          <Button
            variant="outline"
            disabled={resendInvite.isPending || revokeInvite.isPending}
            onClick={() => setConfirmingRevoke(true)}
          >
            {translate("TEAM/ACTION_REVOKE")}
          </Button>
          <AlertDialog
            open={confirmingRevoke}
            onOpenChange={(open) =>
              !revokeInvite.isPending && setConfirmingRevoke(open)
            }
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {translate("SETTINGS/MEMBERS/REVOKE_TITLE")}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {translate("SETTINGS/MEMBERS/REVOKE_DESCRIPTION", {
                    email: member.email,
                  })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={revokeInvite.isPending}>
                  {translate("TEAM/CANCEL")}
                </AlertDialogCancel>
                <PendingButton
                  variant="destructive"
                  pending={revokeInvite.isPending}
                  onClick={() => void revoke(invitationId)}
                >
                  {translate("TEAM/ACTION_REVOKE")}
                </PendingButton>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      }
    />
  )
}

/** The member's permissions, saved together. Mounted once their current
 *  grants have loaded, so the draft starts from them. */
function PermissionsSection({
  member,
  catalog,
  granted,
}: {
  member: TeamMember
  catalog: PermissionCatalog
  granted: string[]
}) {
  const { translate } = useTranslation()
  const updatePermissions = useUpdateMemberPermissions()
  const [draft, setDraft] = useState(granted)
  const dirty =
    draft.length !== granted.length ||
    draft.some((key) => !granted.includes(key))
  useUnsavedChanges(dirty)

  async function save() {
    try {
      await updatePermissions.mutateAsync({
        userSub: member.id,
        permissions: draft,
      })
      toast.success(translate("TEAM/ACCESS_SAVED"))
    } catch (error) {
      toastApiError(error, translate, "TEAM/GENERIC_ERROR")
    }
  }

  return (
    <SettingsSection
      title={translate("SETTINGS/MEMBERS/ACCESS_TITLE")}
      footer={
        <PendingButton
          pending={updatePermissions.isPending}
          disabled={!dirty}
          onClick={() => void save()}
        >
          {translate("TEAM/SAVE_ACCESS")}
        </PendingButton>
      }
    >
      <PermissionEditor catalog={catalog} value={draft} onChange={setDraft} />
    </SettingsSection>
  )
}

function AccessSection({ member }: { member: TeamMember }) {
  const { translate } = useTranslation()
  const isAdmin = member.role === "admin"
  const catalogQuery = useGetPermissionCatalog()
  const grantsQuery = useGetMemberPermissions(member.id, !isAdmin)
  const catalog = catalogQuery.data
  const grants = grantsQuery.data

  if (isAdmin) {
    return (
      <SettingsSection
        title={translate("TEAM/ADMIN_ACCESS_TITLE")}
        description={translate("TEAM/ADMIN_ACCESS_BODY")}
      />
    )
  }
  // The editor starts from the grants it is given, so wait for this
  // opening's own fetch: cached grants may predate another admin's change.
  if (!catalog || !grants || !grantsQuery.isFetchedAfterMount) {
    const failed =
      (!catalog && catalogQuery.isError) || (!grants && grantsQuery.isError)
    return (
      <SettingsSection title={translate("SETTINGS/MEMBERS/ACCESS_TITLE")}>
        {failed ? (
          <SettingsLoadError
            pending={catalogQuery.isFetching || grantsQuery.isFetching}
            onRetry={() => {
              if (!catalog) void catalogQuery.refetch()
              if (!grants) void grantsQuery.refetch()
            }}
          />
        ) : (
          <SettingsSkeleton />
        )}
      </SettingsSection>
    )
  }
  return (
    <PermissionsSection
      member={member}
      catalog={catalog}
      granted={grants.permissions}
    />
  )
}

/** One member's role, invitation and permissions, beside the member list. */
export function MemberAccessSheet({
  member,
  onClose,
  onRequestRemove,
}: {
  member: TeamMember | null
  onClose: () => void
  onRequestRemove: (member: TeamMember) => void
}) {
  const { translate } = useTranslation()
  // Still rendered while the sheet slides out after `member` went null.
  const [shown, setShown] = useState(member)
  if (member && member !== shown) setShown(member)

  return (
    <Sheet open={!!member} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full flex-col overflow-y-auto sm:max-w-xl">
        {shown && (
          // Keyed: switching members starts every draft afresh.
          <div key={shown.id} className="flex flex-col gap-6">
            <SheetHeader>
              <SheetTitle>{shown.email}</SheetTitle>
              <SheetDescription>
                {translate(
                  shown.status === "pending"
                    ? "TEAM/STATUS_PENDING"
                    : "TEAM/STATUS_ACTIVE",
                )}
              </SheetDescription>
            </SheetHeader>
            <div className="flex flex-col divide-y">
              <RoleSection member={shown} />
              {shown.status === "pending" && (
                <InvitationSection member={shown} />
              )}
              <AccessSection member={shown} />
              {/* Like before: an admin is made a member first, not removed. */}
              {!shown.isSelf && shown.role !== "admin" && (
                <SettingsSection
                  title={translate("SETTINGS/MEMBERS/REMOVE_TITLE")}
                  description={translate("TEAM/REMOVE_DESCRIPTION")}
                  footer={
                    <Button
                      variant="outline"
                      onClick={() => onRequestRemove(shown)}
                    >
                      {translate("TEAM/ACTION_REMOVE")}
                    </Button>
                  }
                />
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
